import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "./ui/UIElements/Icon";
import { Button, IconButton } from "./ui/UIElements/Button";
// Cross-app navigation removed - Connect consolidated into Dashboard
import { usePostHog } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";

const ORDERING_LOCKUP_LIGHT =
  "/brand/ordering/1.2.1/logos/ordering-lockup-color.svg";
const ORDERING_LOCKUP_DARK =
  "/brand/ordering/1.2.1/logos/ordering-lockup-color-reversed.svg";
const ORDERING_SYMBOL_LIGHT =
  "/brand/ordering/1.2.1/logos/ordering-symbol-contained-light.svg";
const ORDERING_SYMBOL_DARK =
  "/brand/ordering/1.2.1/logos/ordering-symbol-contained-dark.svg";

const Sidebar = ({ onClose, isCollapsed }) => {
  const { t } = useTranslation("navigation");
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoveredMenuId, setHoveredMenuId] = useState(null);
  const navRef = useRef(null);
  const { isFeatureEnabled } = usePostHog();
  const stripeConnectPluginEnabled = isFeatureEnabled("stripe_connect_link");
  const campaignsEnabled = isFeatureEnabled("campaigns");
  // const metaAdsEnabled = isFeatureEnabled("meta_ads_enabled");
  const deliveryMenuEnabled = isFeatureEnabled("delivery_menu_enabled");
  // const menuImportersEnabled = isFeatureEnabled("menu_importers_enabled");

  const metaAdsEnabled = true;
  const menuImportersEnabled = true;

  const navigationItems = [
    {
      id: "overview",
      label: t("sidebar.overview"),
      icon: "BarChart3",
      path: "/overview",
    },
    {
      id: "ai-agents",
      label: t("sidebar.aiAgents"),
      icon: "Bot",
      path: "/ai-agents",
    },
    {
      id: "orders",
      label: t("sidebar.orders"),
      icon: "ShoppingCart",
      path: "/orders",
    },
    {
      id: "users",
      label: t("sidebar.customers"),
      icon: "Users",
      path: "/users",
    },
    {
      id: "analyze",
      label: t("sidebar.analytics"),
      icon: "TrendingUp",
      hasSubmenu: true,
      submenu: [
        {
          id: "conversations",
          label: t("sidebar.conversations"),
          icon: "MessageCircle",
          path: "/analyze/conversations",
        },
        {
          id: "reports",
          label: t("sidebar.reports"),
          icon: "FileText",
          path: "/analyze/reports",
        },
      ],
    },
    // Store Manager Section (migrated from Connect app)
    {
      id: "store-manager",
      label: t("sidebar.storeManager"),
      icon: "Store",
      hasSubmenu: true,
      submenu: [
        {
          id: "stores",
          label: t("sidebar.stores"),
          icon: "Building2",
          path: "/menu/stores",
        },
        {
          id: "products",
          label: t("sidebar.products"),
          icon: "Package",
          path: "/menu/products",
        },
        {
          id: "brands",
          label: t("sidebar.brands"),
          icon: "Bookmark",
          path: "/menu/brands",
        },
      ],
    },
    // Growth Section (Discounts + future items)
    {
      id: "growth",
      label: t("sidebar.growth"),
      icon: "Rocket",
      hasSubmenu: true,
      submenu: [
        {
          id: "discounts",
          label: t("sidebar.discounts"),
          icon: "Tag",
          path: "/discounts",
        },
        {
          id: "cart-recovery",
          label: t("sidebar.cartRecovery"),
          icon: "RefreshCcw",
          path: "/growth/cart-recovery",
        },
        {
          id: "link-in-bio",
          label: t("sidebar.linkInBio"),
          icon: "Link2",
          path: "/growth/link-in-bio",
        },
        ...(metaAdsEnabled
          ? [
              {
                id: "meta-ads",
                label: t("sidebar.metaAds"),
                icon: "Share2",
                path: "/growth/meta-ads",
              },
            ]
          : []),
        ...(campaignsEnabled
          ? [
              {
                id: "campaigns",
                label: t("sidebar.campaigns"),
                icon: "Megaphone",
                path: "/campaigns",
              },
            ]
          : []),
      ],
    },
    // Delivery Section (migrated from Connect app) - feature flagged
    ...(deliveryMenuEnabled
      ? [
          {
            id: "delivery",
            label: t("sidebar.delivery"),
            icon: "Truck",
            hasSubmenu: true,
            submenu: [
              {
                id: "drivers-managers",
                label: t("sidebar.driversManagers"),
                icon: "UserCog",
                path: "/delivery/drivers-managers",
              },
              {
                id: "delivery-groups",
                label: t("sidebar.deliveryGroups"),
                icon: "Users",
                path: "/delivery/groups",
              },
            ],
          },
        ]
      : []),
    // Integrations Section (migrated from Connect app)
    {
      id: "integrations",
      label: t("sidebar.integrations"),
      icon: "Puzzle",
      hasSubmenu: true,
      submenu: [
        {
          id: "pos",
          label: t("sidebar.posSystems"),
          icon: "Monitor",
          path: "/integrations/pos",
        },
        ...(menuImportersEnabled
          ? [
              {
                id: "menu-importers",
                label: t("sidebar.menuImporters"),
                icon: "Download",
                path: "/integrations/menu-importers",
              },
            ]
          : []),
        {
          id: "sales-channels",
          label: t("sidebar.salesChannels"),
          icon: "ShoppingBag",
          path: "/integrations/sales-channels",
        },
        {
          id: "delivery-services",
          label: t("sidebar.deliveryServices"),
          icon: "Truck",
          path: "/integrations/delivery-services",
        },
      ],
    },
    {
      id: "settings",
      label: t("sidebar.settings"),
      icon: "Settings",
      hasSubmenu: true,
      submenu: [
        {
          id: "settings-basic",
          label: t("sidebar.general"),
          icon: "Globe",
          path: "/settings/basic",
        },
        ...(stripeConnectPluginEnabled
          ? [
              {
                id: "settings-online-payments",
                label: t("sidebar.onlinePayments"),
                icon: "CreditCard",
                path: "/settings/online-payments",
              },
            ]
          : []),
        {
          id: "settings-agent-translations",
          label: t("sidebar.agentTranslations"),
          icon: "Languages",
          path: "/settings/agent-translations",
        },
        {
          id: "settings-admins",
          label: t("sidebar.admins"),
          icon: "Shield",
          path: "/settings/admins",
        },
      ],
    },
    {
      id: "support",
      label: t("sidebar.support"),
      icon: "HelpCircle",
      path: "/support",
    },
  ];

  const [prevCollapsed, setPrevCollapsed] = useState(isCollapsed);
  if (isCollapsed && !prevCollapsed) {
    setPrevCollapsed(true);
    setExpandedMenus({});
  } else if (!isCollapsed && prevCollapsed) {
    setPrevCollapsed(false);
  }

  useEffect(() => {
    const currentPath = location.pathname;

    navigationItems.forEach((item) => {
      if (item.hasSubmenu) {
        const isActive = item.submenu.some(
          (subItem) =>
            currentPath === subItem.path ||
            currentPath.startsWith(subItem.path + "/"),
        );

        if (isActive) {
          setExpandedMenus((prev) => ({
            ...prev,
            [item.id]: true,
          }));
        }
      }
    });
  }, [location.pathname]);

  const closeAllMenus = useCallback(() => {
    setExpandedMenus({});
  }, []);

  useEffect(() => {
    if (!isCollapsed) return;
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        closeAllMenus();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCollapsed, closeAllMenus]);

  const handleNavigation = () => {
    // Cerrar el sidebar en móvil después de navegar
    if (onClose) {
      onClose();
    }
  };

  const toggleSubmenu = (itemId) => {
    setExpandedMenus((prev) => {
      const isOpening = !prev[itemId];
      if (isCollapsed && isOpening) {
        // En modo compacto, solo un flyout abierto a la vez
        return { [itemId]: true };
      }
      return {
        ...prev,
        [itemId]: !prev[itemId],
      };
    });
  };

  const isSubmenuActive = (submenu) => {
    return submenu.some(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + "/"),
    );
  };

  const isItemActive = (item) => {
    if (item.externalUrl) {
      return false;
    }
    if (item.hasSubmenu) {
      return isSubmenuActive(item.submenu);
    }
    return (
      item.path &&
      (location.pathname === item.path ||
        location.pathname.startsWith(item.path + "/"))
    );
  };

  return (
    <div className="flex h-full flex-col">
      {/* Logo Section */}
      <div className="ordering-shell__logo-section sidebar-logo-section flex h-16 items-center justify-between border-b px-2">
        <span id="ordering-sidebar-title" className="sr-only">
          {t("sidebar.navigation")}
        </span>
        <Link
          id="ordering-sidebar-identity"
          to="/overview"
          aria-label={t("sidebar.home")}
          className="ordering-shell__control flex min-h-11 w-full items-center justify-center"
          onClick={handleNavigation}
        >
          {isCollapsed ? (
            <div className="relative flex h-8 w-8 items-center justify-center">
              <img
                src={ORDERING_SYMBOL_LIGHT}
                alt=""
                className="ordering-shell__logo--light h-7 w-7 object-contain"
              />
              <img
                src={ORDERING_SYMBOL_DARK}
                alt=""
                className="ordering-shell__logo--dark h-7 w-7 object-contain"
              />
            </div>
          ) : (
            <div className="relative flex h-9 min-w-[120px] items-center justify-center px-2">
              <img
                src={ORDERING_LOCKUP_LIGHT}
                alt=""
                className="ordering-shell__logo--light h-7 w-auto max-w-[144px] object-contain"
              />
              <img
                src={ORDERING_LOCKUP_DARK}
                alt=""
                className="ordering-shell__logo--dark h-7 w-auto max-w-[144px] object-contain"
              />
            </div>
          )}
        </Link>
        {/* Mobile close button */}
        <IconButton
          type="button"
          onClick={onClose}
          emphasis="ghost"
          intent="neutral"
          size="iconTouch"
          className="ordering-shell__control hover:bg-accent min-h-11 min-w-11 p-2 lg:hidden"
          aria-label={t("sidebar.closeSidebar")}
          data-shell-action="close-sidebar"
        >
          <Icon name="X" className="text-muted-foreground h-5 w-5" />
        </IconButton>
      </div>

      {/* Navigation */}
      <nav
        ref={navRef}
        aria-label={t("sidebar.navigation")}
        className={`min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain py-4 ${
          isCollapsed ? "px-2 lg:px-2" : "px-4"
        }`}
      >
        {navigationItems.map((item) => {
          const isActive = isItemActive(item);
          const isExpanded = expandedMenus[item.id];
          const anyExpanded =
            isCollapsed && Object.values(expandedMenus).some(Boolean);
          const isCollapsedPopoverVisible = Boolean(
            isExpanded || (isCollapsed && hoveredMenuId === item.id),
          );
          const submenuId = `ordering-sidebar-submenu-${item.id}`;
          const submenuTriggerId = `ordering-sidebar-trigger-${item.id}`;

          return (
            <div
              key={item.id}
              className="group relative"
              onMouseEnter={() => {
                if (isCollapsed && item.hasSubmenu && !anyExpanded) {
                  setHoveredMenuId(item.id);
                }
              }}
              onMouseLeave={() => {
                if (hoveredMenuId === item.id) setHoveredMenuId(null);
              }}
            >
              {item.hasSubmenu ? (
                <Button
                  id={submenuTriggerId}
                  type="button"
                  onClick={() => toggleSubmenu(item.id)}
                  variant="ghost"
                  size={null}
                  aria-label={isCollapsed ? item.label : undefined}
                  aria-expanded={
                    isCollapsed
                      ? isCollapsedPopoverVisible
                      : Boolean(isExpanded)
                  }
                  aria-controls={submenuId}
                  data-active={isActive ? "true" : "false"}
                  className={`ordering-shell__nav-item flex w-full items-center ${
                    isCollapsed
                      ? "justify-between px-2 lg:justify-center lg:px-2"
                      : "justify-between px-2"
                  } text-[13px] font-normal`}
                >
                  <div
                    className={`flex items-center ${
                      isCollapsed ? "lg:justify-center" : ""
                    }`}
                  >
                    <Icon name={item.icon} className="h-4 w-4" />
                    <span
                      className={`${
                        isCollapsed ? "lg:hidden" : ""
                      } ms-3 whitespace-nowrap`}
                    >
                      {item.label}
                    </span>
                  </div>
                  {!isCollapsed && (
                    <Icon
                      name="ChevronDown"
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </Button>
              ) : item.externalUrl ? (
                <a
                  href={item.externalUrl}
                  // target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleNavigation}
                  aria-label={isCollapsed ? item.label : undefined}
                  className={`ordering-shell__nav-item flex w-full items-center ${
                    isCollapsed
                      ? "justify-between px-2 lg:justify-center lg:px-2"
                      : "justify-between px-2"
                  } text-[13px] font-normal`}
                >
                  <div
                    className={`flex items-center ${
                      isCollapsed ? "lg:justify-center" : ""
                    }`}
                  >
                    <Icon name={item.icon} className="h-4 w-4" />
                    <span
                      className={`${
                        isCollapsed ? "lg:hidden" : ""
                      } ms-3 whitespace-nowrap`}
                    >
                      {item.label}
                    </span>
                  </div>
                </a>
              ) : (
                <Link
                  to={item.path}
                  onClick={handleNavigation}
                  aria-label={isCollapsed ? item.label : undefined}
                  aria-current={isActive ? "page" : undefined}
                  data-active={isActive ? "true" : "false"}
                  className={`ordering-shell__nav-item flex w-full items-center ${
                    isCollapsed
                      ? "justify-between px-2 lg:justify-center lg:px-2"
                      : "justify-between px-2"
                  } text-[13px] font-normal`}
                >
                  <div
                    className={`flex items-center ${
                      isCollapsed ? "lg:justify-center" : ""
                    }`}
                  >
                    <Icon name={item.icon} className="h-4 w-4" />
                    <span
                      className={`${
                        isCollapsed ? "lg:hidden" : ""
                      } ms-3 whitespace-nowrap`}
                    >
                      {item.label}
                    </span>
                  </div>
                </Link>
              )}

              {/* Submenu */}
              {item.hasSubmenu && !isCollapsed && (
                <div
                  id={submenuId}
                  aria-hidden={!isExpanded}
                  inert={!isExpanded}
                  className={`overflow-hidden transition-all duration-200 ${
                    isExpanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="ms-4 mt-1 space-y-0.5">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.id}
                        to={subItem.path}
                        onClick={() => {
                          handleNavigation();
                        }}
                        data-active={
                          location.pathname === subItem.path ||
                          location.pathname.startsWith(subItem.path + "/")
                            ? "true"
                            : "false"
                        }
                        aria-current={
                          location.pathname === subItem.path ||
                          location.pathname.startsWith(subItem.path + "/")
                            ? "page"
                            : undefined
                        }
                        className="ordering-shell__nav-item ordering-shell__submenu-item flex w-full items-center px-2 text-[13px] font-normal"
                      >
                        <Icon name={subItem.icon} className="h-4 w-4" />
                        <span className="ms-3 whitespace-nowrap">
                          {subItem.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Tooltip for collapsed mode - only on desktop */}
              {isCollapsed && !item.hasSubmenu && (
                <div
                  aria-hidden="true"
                  className="ordering-shell__popover pointer-events-none absolute start-full top-0 ms-2 hidden items-center rounded-md border px-3 py-2 text-[13px] whitespace-nowrap opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 lg:flex"
                  style={{ zIndex: "var(--layer-popover)" }}
                >
                  {item.label}
                </div>
              )}

              {/* Popover submenu for collapsed mode (desktop) */}
              {isCollapsed && item.hasSubmenu && (
                <div
                  id={submenuId}
                  className={`ordering-shell__popover absolute start-full top-0 ms-2 hidden w-56 rounded-md border transition-opacity lg:block ${
                    isCollapsedPopoverVisible ? "opacity-100" : "opacity-0"
                  } ${
                    isCollapsedPopoverVisible
                      ? "pointer-events-auto"
                      : "pointer-events-none"
                  }`}
                  role="group"
                  aria-labelledby={submenuTriggerId}
                  aria-hidden={!isCollapsedPopoverVisible}
                  inert={!isCollapsedPopoverVisible}
                  style={{ zIndex: "var(--layer-popover)" }}
                >
                  <div className="border-border border-b px-3 py-2">
                    <div className="text-popover-foreground text-sm font-medium">
                      {item.label}
                    </div>
                  </div>
                  <div className="py-1">
                    {item.submenu.map((subItem) => (
                      <Link
                        key={subItem.id}
                        to={subItem.path}
                        onClick={() => {
                          handleNavigation();
                          setExpandedMenus((prev) => ({
                            ...prev,
                            [item.id]: false,
                          }));
                        }}
                        data-active={
                          location.pathname === subItem.path ||
                          location.pathname.startsWith(subItem.path + "/")
                            ? "true"
                            : "false"
                        }
                        aria-current={
                          location.pathname === subItem.path ||
                          location.pathname.startsWith(subItem.path + "/")
                            ? "page"
                            : undefined
                        }
                        className="ordering-shell__submenu-item flex min-h-11 items-center gap-3 px-3 py-2 text-[13px] transition-colors"
                      >
                        <Icon name={subItem.icon} className="h-4 w-4" />
                        <span className="whitespace-nowrap">
                          {subItem.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer Section - could add user info or additional links here */}
      <div className="border-border border-t p-3">
        <div className="text-muted-foreground text-center text-xs">
          © {new Date().getFullYear()} {t("footer.copyright")}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
